import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateLeadComment, useLeadComments } from '../../hooks/use-lead-comments';
import { dateLocaleFor } from '../../lib/i18n';
import { Avatar, AvatarFallback, initialsFor } from '../ui/avatar';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';

export function Comments({ leadId }: { leadId: string }): JSX.Element {
  const comments = useLeadComments(leadId);
  const create = useCreateLeadComment(leadId);
  const { t, i18n } = useTranslation();
  const locale = dateLocaleFor(i18n.resolvedLanguage ?? i18n.language);
  const [body, setBody] = useState('');

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Textarea
          placeholder={t('comments.placeholder')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!body.trim() || create.isPending}
            onClick={async () => {
              await create.mutateAsync({ body: body.trim() });
              setBody('');
            }}
          >
            {create.isPending ? t('comments.posting') : t('comments.post')}
          </Button>
        </div>
      </div>
      {comments.isLoading ? (
        <Skeleton className="h-20" />
      ) : (
        <ul className="space-y-3">
          {(comments.data?.items ?? []).map((c) => (
            <li key={c.id} className="flex gap-3">
              <Avatar>
                <AvatarFallback>{initialsFor(c.author.fullName)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium">{c.author.fullName}</span>
                  <span className="text-xs text-surface-400">
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true, locale })}
                    {c.editedAt && ` · ${t('comments.edited')}`}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{c.body}</p>
              </div>
            </li>
          ))}
          {comments.data && comments.data.items.length === 0 && (
            <li className="text-sm text-surface-500">{t('comments.empty')}</li>
          )}
        </ul>
      )}
    </div>
  );
}
